import { useEffect, useMemo, useRef, useState } from 'react';
import { AiPrediction, Linkrole, TagOrigin, TagsByDocumentResponse, PresentationTreeResponse } from 'entities/tag';
import { ReferenceDataValue } from 'entities/document';
import { buildConceptOrderIndex, conceptGroupEligibleKeys, groupCheckState, cellKey, rowKey, PredRow } from './aiResultGrouping';
import { FilterToken, AncestorIndex } from './aiLinkroleFilter';

type Suggestion = { row: number; col: number; predictions: AiPrediction[] };
type SuggestResult = { suggestions: Suggestion[] } | undefined;

interface UseAiSuggestionsArgs {
  data: SuggestResult;
  tagsData: TagsByDocumentResponse | undefined;
  refData: ReferenceDataValue | undefined;
  presentationTree: PresentationTreeResponse | null | undefined;
  selectedLinkroles: string[];
  linkroleOverrides: Record<string, { alias?: string; hidden?: boolean }>;
  linkroleRows: (p: AiPrediction) => Linkrole[];
  selectedTokens: FilterToken[];
  ancestorIndex: AncestorIndex;
  textOf: (row: number, col: number) => string;
  rowTextOf: (row: number) => string;
  colTextOf: (row: number, col: number) => string;
}

/**
 * Owns the AI modal's results layer: the applied-tag lookups, the per-linkrole
 * prediction filtering, the checkbox-selection state (auto-pick best-F1, toggle,
 * mark-all, concept-group toggle), the derived "cells to apply" list, and the
 * grouped rows for rendering. Extracted from AiApplicationModal; the applied-tag
 * helpers are internal — the component consumes only the selection + cellGroups.
 */
export function useAiSuggestions({
  data,
  tagsData,
  refData,
  presentationTree,
  selectedLinkroles,
  linkroleOverrides,
  linkroleRows,
  selectedTokens,
  ancestorIndex,
  textOf,
  rowTextOf,
  colTextOf,
}: UseAiSuggestionsArgs) {
  const tagById = useMemo(() => {
    const m = new Map<string, { conceptId: string; origin: TagOrigin; linkroleUri: string | null }>();
    (tagsData?.tags ?? []).forEach((t) => m.set(t.id, { conceptId: t.conceptId, origin: t.origin ?? 'human', linkroleUri: t.linkroleUri ?? null }));
    return m;
  }, [tagsData]);

  const valueOf = (row: number, col: number): string => {
    const cell = refData?.selectionContent?.[row]?.[col];
    if (cell == null) return '';
    return typeof cell === 'string' ? cell : String(cell.value ?? '');
  };
  const appliedTag = (row: number, col: number, conceptId: string): { origin: TagOrigin; linkroleUri: string | null } | null => {
    const ids: string[] = refData?.selectionContent?.[row]?.[col]?.tags ?? [];
    for (const id of ids) {
      const t = tagById.get(id);
      if (t && t.conceptId === conceptId) return { origin: t.origin, linkroleUri: t.linkroleUri ?? null };
    }
    return null;
  };
  const appliedOrigin = (row: number, col: number, conceptId: string, linkroleUri: string): TagOrigin | null => {
    const t = appliedTag(row, col, conceptId);
    if (!t) return null;
    return t.linkroleUri == null || t.linkroleUri === linkroleUri ? t.origin : null;
  };
  const cellHasAnyTag = (row: number, col: number): boolean => (refData?.selectionContent?.[row]?.[col]?.tags ?? []).length > 0;
  const rowsForPrediction = (p: AiPrediction, row: number, col: number): Linkrole[] => {
    const rows = linkroleRows(p);
    const t = appliedTag(row, col, p.conceptId);
    return t && t.linkroleUri != null ? rows.filter((lr) => lr.uri === t.linkroleUri) : rows;
  };

  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!data) return;
    const next = new Set<string>();
    data.suggestions.forEach((s) => {
      if (cellHasAnyTag(s.row, s.col)) return; // #4: don't auto-suggest a replacement for an already-tagged cell
      const candidates = s.predictions.filter((p) => rowsForPrediction(p, s.row, s.col).length > 0);
      if (!candidates.length) return;
      const top = candidates.reduce((a, b) => (b.f1ScoreTest > a.f1ScoreTest ? b : a));
      next.add(rowKey(s.row, s.col, top.conceptId, rowsForPrediction(top, s.row, s.col)[0].uri)); // first visible linkrole
    });
    setChecked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedTokens, ancestorIndex]);

  const toggle = (key: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const markAll = () => {
    if (!data) return;
    const next = new Set<string>();
    data.suggestions.forEach((s) => {
      s.predictions.forEach((p) => {
        const rows = rowsForPrediction(p, s.row, s.col);
        if (!rows.length) return;
        if (appliedOrigin(s.row, s.col, p.conceptId, rows[0].uri) !== null) return;
        next.add(rowKey(s.row, s.col, p.conceptId, rows[0].uri));
      });
    });
    setChecked(next);
  };
  const unmarkAll = () => setChecked(new Set());

  const toggleConceptGroup = (members: PredRow[]) => {
    const eligible = conceptGroupEligibleKeys(members, checked);
    if (!eligible.length) return;
    const allOn = groupCheckState(eligible, checked) === 'all';
    setChecked((prev) => {
      const next = new Set(prev);
      eligible.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const selectedConceptCells = useMemo(() => {
    const map = new Map<string, { row: number; col: number; value?: string; conceptId: string; linkroleUri: string | null }>();
    (data?.suggestions ?? []).forEach((s) => {
      const value = valueOf(s.row, s.col);
      s.predictions.forEach((p) => {
        for (const lr of rowsForPrediction(p, s.row, s.col)) {
          if (appliedOrigin(s.row, s.col, p.conceptId, lr.uri) !== null) continue; // skip already-applied rows
          if (!checked.has(rowKey(s.row, s.col, p.conceptId, lr.uri))) continue;
          map.set(`${s.row}:${s.col}:${p.conceptId}:${lr.uri}`, { row: s.row, col: s.col, value, conceptId: p.conceptId, linkroleUri: lr.uri });
        }
      });
    });
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, checked, tagById, refData, linkroleOverrides]);

  const checkedCount = selectedConceptCells.length;

  const totalSelectable = useMemo(() => {
    let n = 0;
    (data?.suggestions ?? []).forEach((s) =>
      s.predictions.forEach((p) => {
        const rows = rowsForPrediction(p, s.row, s.col);
        if (rows.length > 0 && appliedOrigin(s.row, s.col, p.conceptId, rows[0].uri) === null) n += 1;
      })
    );
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tagById, refData, linkroleOverrides]);
  const allSelected = totalSelectable > 0 && checkedCount === totalSelectable;
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = checkedCount > 0 && checkedCount < totalSelectable;
  }, [checkedCount, totalSelectable]);

  const cellGroups = useMemo(() => {
    return (data?.suggestions ?? []).map((s) => {
      const value = valueOf(s.row, s.col);
      const text = textOf(s.row, s.col);
      const preds: PredRow[] = [];
      s.predictions.forEach((p) => {
        const rows = rowsForPrediction(p, s.row, s.col);
        rows.forEach((lr) => {
          const origin = appliedOrigin(s.row, s.col, p.conceptId, lr.uri);
          const applied = origin !== null;
          preds.push({ kind: 'pred', key: rowKey(s.row, s.col, p.conceptId, lr.uri), row: s.row, col: s.col, value, text, p, lr, rows, applied, origin });
        });
      });
      return { row: s.row, col: s.col, cellKey: cellKey(s.row, s.col), value, text, rowText: rowTextOf(s.row), colText: colTextOf(s.row, s.col), preds };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tagById, refData, linkroleOverrides, selectedLinkroles]);

  const conceptOrderIndex = useMemo(() => buildConceptOrderIndex(presentationTree), [presentationTree]);

  return {
    checked,
    toggle,
    markAll,
    unmarkAll,
    toggleConceptGroup,
    selectedConceptCells,
    checkedCount,
    totalSelectable,
    allSelected,
    selectAllRef,
    cellGroups,
    conceptOrderIndex,
  };
}
